import type { InvestorAllocationsResponse } from '../../models/InvestmentEntity';
import type Investor from '../../models/Investor';
import type BaseBackendService from '../../services/BaseBackendService';
import { mergeInvestorAllocationResponses, preselectAllocationState } from './fundUpdateUtils';

type FundUpdateAllocationsBackend = BaseBackendService & {
  getInvestorAllocations: (
    investorId: string,
    includeNames: boolean,
  ) => Promise<InvestorAllocationsResponse>;
};

export type FundUpdateAllocationsResult =
  | {
      ok: true;
      allocationsData: InvestorAllocationsResponse;
      selectedEntityIds: Set<string>;
      includeUnallocated: boolean;
    }
  | { ok: false; error: string };

export async function fetchFundUpdateAllocations(
  backendService: FundUpdateAllocationsBackend,
  investor: Investor,
): Promise<FundUpdateAllocationsResult> {
  const allInvestorIds = (investor as Investor & { __allInvestorIds?: string[] }).__allInvestorIds;

  try {
    const allocationsData =
      allInvestorIds && allInvestorIds.length > 1
        ? mergeInvestorAllocationResponses(
            await Promise.all(
              allInvestorIds.map((investorId) =>
                backendService.getInvestorAllocations(investorId, true),
              ),
            ),
          )
        : await backendService.getInvestorAllocations(investor.id, true);

    const { selectedEntityIds, includeUnallocated } = preselectAllocationState(allocationsData);

    return {
      ok: true,
      allocationsData,
      selectedEntityIds,
      includeUnallocated,
    };
  } catch (error) {
    console.error('Failed to fetch allocations:', error);
    return { ok: false, error: 'Unable to load allocation data' };
  }
}
